import { ModalBackdrop } from './ModalFrame';
import { ModalCloseButton } from './ModalCloseButton';
import { GlobalSearch } from './GlobalSearch';
import type { GlobalSearchResult } from './global-search-api';
import { PaginationPreferences } from './pagination-preferences';
import { useRememberedSection } from './navigation-preferences';
import { DashboardLiveMetrics } from './DashboardLiveMetrics';
import { HostingPleskAlerts } from './HostingPleskAlerts';
import { HostingServersPanel } from './HostingServersPanel';
import { Fragment, useEffect, useState } from 'react';
import type { SessionViewerOrganizationRole, SessionViewerResult } from '../auth/api';
import authForge from '../assets/auth/tosho-login-forge.webp';
import toshoLogo from '../assets/tosho-logo.svg';
import { TOSHO_PRODUCT_CHANGELOG } from '../product-changelog';
import { TOSHO_PRODUCT_VERSION } from '../product-version';
import { ConnectionsHub } from './ConnectionsHub';
import { freebeIntegrationApi } from './freebe-integration-api';
import { BnpConnectionPanel } from './BnpConnectionPanel';
import { ClientDirectoryPanel } from './ClientDirectoryPanel';
import { ProviderDirectoryPanel } from './ProviderDirectoryPanel';
import { FreebeIntegrationPanel } from './FreebeIntegrationPanel';
import { GoogleDriveIntegrationPanel } from './GoogleDriveIntegrationPanel';
import { FinancialOverviewPanel } from './FinancialOverviewPanel';
import { MyAccountDialog } from './MyAccountDialog';
import { PrototypeIcon, type PrototypeIconName } from './PrototypeIcon';
import { clientDirectoryApi, type ClientDirectory } from './client-directory-api';

type OrganizationSection =
  | 'dashboard'
  | 'connections'
  | 'freebe'
  | 'bnp'
  | 'drive'
  | 'clients'
  | 'providers'
  | 'finance'
  | 'servers';

interface DashboardShellProps {
  readonly viewer: SessionViewerResult;
  readonly csrfToken: string;
  readonly onReauthenticate: () => void;
  readonly onLogout: () => void;
  readonly logoutPending?: boolean;
  readonly notificationCount?: number;
}

interface NavigationItem {
  readonly label: string;
  readonly icon: PrototypeIconName;
  readonly section?: OrganizationSection;
  readonly active?: boolean;
}

interface NavigationSection {
  readonly label?: string;
  readonly items: readonly NavigationItem[];
}

const COMPACT_SIDEBAR_MEDIA = '(min-width: 901px) and (max-width: 1434px)';

const administratorNavigation: readonly NavigationSection[] = [
  {
    label: 'Pilotage',
    items: [
      { label: 'Tableau de bord', icon: 'layout-grid', section: 'dashboard' },
      { label: 'Projets', icon: 'folder-kanban' },
      { label: 'SAV', icon: 'life-buoy' },
      { label: 'FAQ', icon: 'book-open' },
      { label: 'Clients', icon: 'users-round', section: 'clients' },
      { label: 'Prestataires', icon: 'briefcase-business', section: 'providers' },
    ],
  },
  {
    label: 'Co-pilotage',
    items: [
      { label: 'Finances & stats', icon: 'chart-column', section: 'finance' },
      { label: 'Knowledge Base', icon: 'sparkles' },
    ],
  },
  {
    label: 'Réglages',
    items: [
      { label: 'Configuration', icon: 'settings' },
      { label: 'Connexions / API', icon: 'network', section: 'connections' },
      { label: 'Serveurs reliés', icon: 'database', section: 'servers' },
    ],
  },
];

const providerNavigation: readonly NavigationSection[] = [
  {
    label: 'Pilotage',
    items: [
      { label: 'Tableau de bord', icon: 'layout-grid', section: 'dashboard' },
      { label: 'Projets', icon: 'folder-kanban' },
      { label: 'SAV', icon: 'life-buoy' },
      { label: 'FAQ', icon: 'book-open' },
    ],
  },
  { label: 'Co-pilotage', items: [{ label: 'Knowledge Base', icon: 'sparkles' }] },
];

const clientNavigation: readonly NavigationSection[] = [
  {
    label: 'Pilotage',
    items: [
      { label: 'Tableau de bord', icon: 'layout-grid', section: 'dashboard' },
      { label: 'Projets', icon: 'folder-kanban' },
      { label: 'SAV', icon: 'life-buoy' },
      { label: 'FAQ', icon: 'book-open' },
    ],
  },
];

const supervisorNavigation: readonly NavigationSection[] = [
  {
    items: [
      { label: 'Administrateurs', icon: 'users-round', active: true },
      { label: 'Configuration', icon: 'settings' },
    ],
  },
];

const summaryCards = [
  { label: 'Prospects', icon: 'user-round-help', className: 'dashboard-summary-card--neutral' },
  { label: 'Clients', icon: 'users-round', className: 'dashboard-summary-card--client' },
  { label: 'Projets actifs', icon: 'folder-kanban', className: 'dashboard-summary-card--project' },
  { label: 'Tickets SAV', icon: 'life-buoy', className: 'dashboard-summary-card--sav' },
  { label: 'Tutoriels FAQ', icon: 'book-open', className: 'dashboard-summary-card--faq' },
] as const satisfies readonly {
  readonly label: string;
  readonly icon: PrototypeIconName;
  readonly className: string;
}[];

function navigationForViewer(
  viewer: SessionViewerResult,
  displayRole: SessionViewerOrganizationRole | undefined,
): readonly NavigationSection[] {
  if (viewer.accountKind === 'PLATFORM_SUPERVISOR') return supervisorNavigation;
  switch (displayRole) {
    case 'ORGANIZATION_ADMIN':
      return administratorNavigation;
    case 'PROVIDER':
      return providerNavigation;
    case 'CLIENT':
      return clientNavigation;
    default:
      return [];
  }
}

function organizationRoleLabel(role: SessionViewerOrganizationRole | undefined): string {
  switch (role) {
    case 'ORGANIZATION_ADMIN':
      return 'Administrateur';
    case 'PROVIDER':
      return 'Prestataire';
    case 'CLIENT':
      return 'Client';
    default:
      return 'Membre';
  }
}

function shouldStartWithCompactSidebar(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(COMPACT_SIDEBAR_MEDIA).matches;
}

function useCloseOnEscape(onClose: () => void): void {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);
}

/**
 * Shell connecté commun à tous les profils Tosho.
 *
 * Les différences de rôle pilotent uniquement la présentation autorisée. Le sélecteur « Voir
 * comme » d'un administrateur est une prévisualisation locale du shell : il ne modifie jamais la
 * session ni les droits serveur. Les données métier restent absentes tant que leurs sources réelles
 * ne sont pas raccordées.
 */
function DashboardContent({
  viewer,
  csrfToken,
  onReauthenticate,
  onLogout,
  logoutPending = false,
  notificationCount = 0,
}: DashboardShellProps): React.JSX.Element {
  const isSupervisor = viewer.accountKind === 'PLATFORM_SUPERVISOR';
  const isOrganizationAdministrator = viewer.organization?.role === 'ORGANIZATION_ADMIN';
  const [previewRole, setPreviewRole] = useState<SessionViewerOrganizationRole>(
    viewer.organization?.role ?? 'ORGANIZATION_ADMIN',
  );
  const connectorReturn =
    typeof window !== 'undefined' && window.location.hash === '#google-drive'
      ? 'drive'
      : typeof window !== 'undefined' &&
          new URLSearchParams(window.location.search).get('section') === 'freebe'
        ? 'freebe'
        : null;
  const [activeSection, setActiveSection] = useRememberedSection<OrganizationSection>(
    'section',
    isOrganizationAdministrator && connectorReturn ? connectorReturn : 'dashboard',
    isOrganizationAdministrator
      ? [
          'dashboard',
          'connections',
          'freebe',
          'bnp',
          'drive',
          'clients',
          'providers',
          'finance',
          'servers',
        ]
      : ['dashboard'],
    connectorReturn !== null,
  );
  const [freebeMfaCount, setFreebeMfaCount] = useState(0);
  useEffect(() => {
    if (!isOrganizationAdministrator) return;
    // Le marqueur OAuth indique un retour ponctuel, pas une préférence éternelle.
    // replaceState ne déclenche pas popstate et conserve le shell authentifié.
    const url = new URL(window.location.href);
    let consumed = false;
    if (url.hash === '#google-drive') {
      url.hash = '';
      consumed = true;
    }
    if (url.searchParams.get('section') === 'freebe') {
      url.searchParams.delete('section');
      consumed = true;
    }
    if (consumed) window.history.replaceState(window.history.state, '', url);
    let active = true;
    const poll = (): void => {
      void freebeIntegrationApi
        .pendingMfa()
        .then((items) => {
          if (active && Array.isArray(items)) setFreebeMfaCount(items.length);
        })
        .catch(() => undefined);
    };
    poll();
    const timer = window.setInterval(poll, 3000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [isOrganizationAdministrator]);
  const [sectionVisit, setSectionVisit] = useState(0);
  const [directoryInitialScreen, setDirectoryInitialScreen] = useState<
    'overview' | 'clients' | 'prospects'
  >('overview');
  const [whyToshoOpen, setWhyToshoOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTarget, setSearchTarget] = useState<GlobalSearchResult | null>(null);
  const chooseSearch = (result: GlobalSearchResult): void => {
    setDirectoryInitialScreen('overview');
    setSearchTarget(result);
    setActiveSection(result.section);
    setSectionVisit((n) => n + 1);
    setSearchOpen(false);
  };
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(shouldStartWithCompactSidebar);
  const [sidebarManuallySet, setSidebarManuallySet] = useState(false);

  useEffect(() => {
    if (sidebarManuallySet) return undefined;

    const compactMedia = window.matchMedia(COMPACT_SIDEBAR_MEDIA);
    const syncAutomaticState = (): void => setSidebarCollapsed(compactMedia.matches);
    syncAutomaticState();
    compactMedia.addEventListener('change', syncAutomaticState);
    return () => compactMedia.removeEventListener('change', syncAutomaticState);
  }, [sidebarManuallySet]);

  useEffect(() => {
    if (previewRole !== 'ORGANIZATION_ADMIN') setActiveSection('dashboard');
  }, [previewRole]);

  const displayRole = isOrganizationAdministrator ? previewRole : viewer.organization?.role;
  const navigation = navigationForViewer(viewer, displayRole);
  const profileLabel = isSupervisor
    ? 'Superviseur de plateforme'
    : organizationRoleLabel(viewer.organization?.role);
  const organizationLogoUrl = viewer.organization?.branding.logoUrl ?? null;
  const organizationDisplayName = viewer.organization?.displayName ?? 'Organisation';
  const safeNotificationCount = Math.max(0, Math.floor(notificationCount)) + freebeMfaCount;

  const toggleSidebar = (): void => {
    setSidebarManuallySet(true);
    setSidebarCollapsed((currentState) => !currentState);
  };

  return (
    <>
      <main className={`l-app-shell${sidebarCollapsed ? ' is-sidebar-collapsed' : ''}`}>
        <aside className="app-sidebar" aria-label="Navigation principale">
          {!isSupervisor && organizationLogoUrl ? (
            <header
              className="app-sidebar__brand"
              aria-label={`Organisation ${organizationDisplayName}`}
            >
              <img
                className="app-sidebar__organization-logo"
                src={organizationLogoUrl}
                alt={organizationDisplayName}
              />
            </header>
          ) : null}

          <nav className="app-navigation">
            {navigation.map((section, sectionIndex) => (
              <div
                className="app-navigation__group"
                key={`${section.label ?? 'root'}-${sectionIndex}`}
              >
                {section.label ? <p className="app-navigation__section">{section.label}</p> : null}
                {section.items.map((item) => {
                  const isActive =
                    item.active === true ||
                    (item.section !== undefined && item.section === activeSection) ||
                    (item.section === 'connections' &&
                      (activeSection === 'freebe' ||
                        activeSection === 'bnp' ||
                        activeSection === 'drive'));
                  return (
                    <button
                      key={item.label}
                      className={`app-navigation__item${isActive ? ' is-active' : ''}`}
                      type="button"
                      disabled={item.section === undefined && item.active !== true}
                      aria-current={isActive ? 'page' : undefined}
                      aria-label={item.label}
                      data-tooltip={item.label}
                      onClick={() => {
                        if (item.section !== undefined) {
                          if (item.section === activeSection) setSectionVisit((visit) => visit + 1);
                          setSearchTarget(null);
                          setDirectoryInitialScreen('overview');
                          setActiveSection(item.section);
                        }
                      }}
                    >
                      <PrototypeIcon className="app-navigation__icon" name={item.icon} />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          <div className="app-sidebar__lower">
            <div className="app-sidebar__tosho-footer" aria-label="Tosho">
              <span className="app-sidebar__logo-row">
                <img className="app-sidebar__tosho-logo" src={toshoLogo} alt="Tosho" />
                <button
                  className="app-sidebar__why-tosho"
                  type="button"
                  onClick={() => setWhyToshoOpen(true)}
                  aria-label="Pourquoi Tosho ?"
                  aria-haspopup="dialog"
                  title="Pourquoi Tosho ?"
                >
                  <PrototypeIcon name="circle-help" />
                </button>
              </span>
              <span className="app-sidebar__version-row">
                <small>{TOSHO_PRODUCT_VERSION}</small>
                <button
                  className="app-sidebar__changelog"
                  type="button"
                  onClick={() => setChangelogOpen(true)}
                  aria-label="Voir le changelog Tosho"
                  aria-haspopup="dialog"
                  title="Changelog"
                >
                  <PrototypeIcon name="history" />
                </button>
              </span>
            </div>

            <div className="app-sidebar__footer">
              <PrototypeIcon className="app-sidebar__security-icon" name="shield-check" />
              <span>
                <strong>Accès sécurisé</strong>
                <small>
                  Tosho protège vos données confidentielles avec les dernières technologies de
                  chiffrement et d’identification.
                </small>
              </span>
            </div>
          </div>
        </aside>

        <button
          className="app-sidebar__toggle"
          type="button"
          onClick={toggleSidebar}
          aria-label={sidebarCollapsed ? 'Déplier le menu latéral' : 'Replier le menu latéral'}
          aria-pressed={sidebarCollapsed}
          title={sidebarCollapsed ? 'Déplier le menu' : 'Replier le menu'}
        >
          <PrototypeIcon className="app-sidebar__toggle-icon" name="chevron-down" />
        </button>

        <section className="l-app-main">
          <header className="app-topbar">
            <GlobalSearch
              administrator={displayRole === 'ORGANIZATION_ADMIN'}
              onChoose={chooseSearch}
            />

            <button
              className="app-search-trigger"
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="Ouvrir la recherche globale"
              aria-haspopup="dialog"
            >
              <PrototypeIcon name="search" />
            </button>

            <div className="app-topbar__actions">
              {isOrganizationAdministrator ? (
                <label className="app-view-as">
                  <span>Voir comme</span>
                  <select
                    value={previewRole}
                    onChange={(event) =>
                      setPreviewRole(event.currentTarget.value as SessionViewerOrganizationRole)
                    }
                    aria-label="Voir comme"
                  >
                    <option value="ORGANIZATION_ADMIN">Administrateur</option>
                    <option value="PROVIDER">Prestataire</option>
                    <option value="CLIENT">Client</option>
                  </select>
                </label>
              ) : null}

              <button
                className="app-icon-button app-notifications"
                type="button"
                disabled={freebeMfaCount === 0 || displayRole !== 'ORGANIZATION_ADMIN'}
                onClick={() => {
                  setActiveSection('freebe');
                  setSectionVisit((visit) => visit + 1);
                }}
                aria-label="Notifications"
              >
                <PrototypeIcon name="bell" />
                {safeNotificationCount > 0 ? (
                  <span className="app-notifications__badge">
                    {safeNotificationCount > 99 ? '99+' : safeNotificationCount}
                  </span>
                ) : null}
              </button>

              <details className="app-profile">
                <summary className="app-profile__summary">
                  <span className="app-profile__avatar" aria-hidden="true">
                    {viewer.givenName.slice(0, 1).toUpperCase() || 'T'}
                  </span>
                  <span className="app-profile__identity">
                    <strong>
                      {viewer.givenName} {viewer.familyName}
                    </strong>
                    <small>{profileLabel}</small>
                  </span>
                  <PrototypeIcon className="app-profile__chevron" name="chevron-down" />
                </summary>
                <div className="app-profile__menu">
                  <button
                    className="app-profile__account"
                    type="button"
                    onClick={() => setAccountOpen(true)}
                  >
                    Mon compte
                  </button>
                  <button
                    className="app-profile__logout"
                    type="button"
                    onClick={onLogout}
                    disabled={logoutPending}
                  >
                    {logoutPending ? 'Déconnexion…' : 'Déconnexion'}
                  </button>
                </div>
              </details>
            </div>
          </header>

          {isSupervisor ? (
            <SupervisorHome viewer={viewer} />
          ) : displayRole === 'ORGANIZATION_ADMIN' && activeSection === 'connections' ? (
            <ConnectionsHub
              onOpenFreebe={() => setActiveSection('freebe')}
              onOpenBnp={() => setActiveSection('bnp')}
              onOpenDrive={() => setActiveSection('drive')}
            />
          ) : displayRole === 'ORGANIZATION_ADMIN' && activeSection === 'drive' ? (
            <GoogleDriveIntegrationPanel
              csrfToken={csrfToken}
              onBack={() => setActiveSection('connections')}
              onReauthenticate={onReauthenticate}
            />
          ) : displayRole === 'ORGANIZATION_ADMIN' && activeSection === 'freebe' ? (
            <FreebeIntegrationPanel csrfToken={csrfToken} />
          ) : displayRole === 'ORGANIZATION_ADMIN' && activeSection === 'bnp' ? (
            <BnpConnectionPanel
              csrfToken={csrfToken}
              onBack={() => setActiveSection('connections')}
            />
          ) : displayRole === 'ORGANIZATION_ADMIN' && activeSection === 'clients' ? (
            <ClientDirectoryPanel
              key={sectionVisit}
              csrfToken={csrfToken}
              initialClient={searchTarget?.client ?? null}
              initialScreen={directoryInitialScreen}
            />
          ) : displayRole === 'ORGANIZATION_ADMIN' && activeSection === 'servers' ? (
            <HostingServersPanel
              key={sectionVisit}
              csrfToken={csrfToken}
              initialServer={searchTarget?.server ?? null}
              initialSite={searchTarget?.site ?? null}
              initialView={searchTarget?.view}
              organizationLabel={viewer.organization?.displayName ?? 'Votre organisation'}
              organizationLogoUrl={organizationLogoUrl}
              onReauthenticate={onReauthenticate}
            />
          ) : displayRole === 'ORGANIZATION_ADMIN' && activeSection === 'providers' ? (
            <ProviderDirectoryPanel
              key={sectionVisit}
              csrfToken={csrfToken}
              initialProvider={searchTarget?.provider ?? null}
            />
          ) : displayRole === 'ORGANIZATION_ADMIN' && activeSection === 'finance' ? (
            <FinancialOverviewPanel
              csrfToken={csrfToken}
              onOpenConnections={() => setActiveSection('connections')}
            />
          ) : (
            <OrganizationDashboard
              viewer={viewer}
              displayRole={displayRole}
              onNavigate={(section, directoryScreen) => {
                setSearchTarget(null);
                setDirectoryInitialScreen(directoryScreen ?? 'overview');
                setActiveSection(section);
                setSectionVisit((visit) => visit + 1);
              }}
            />
          )}
        </section>
      </main>

      {searchOpen ? (
        <GlobalSearchDialog
          administrator={displayRole === 'ORGANIZATION_ADMIN'}
          onChoose={chooseSearch}
          onClose={() => setSearchOpen(false)}
        />
      ) : null}
      {changelogOpen ? <ChangelogDialog onClose={() => setChangelogOpen(false)} /> : null}
      {whyToshoOpen ? <WhyToshoDialog onClose={() => setWhyToshoOpen(false)} /> : null}
      {accountOpen ? (
        <MyAccountDialog viewer={viewer} onClose={() => setAccountOpen(false)} />
      ) : null}
    </>
  );
}

function OrganizationDashboard({
  viewer,
  displayRole,
  onNavigate,
}: {
  readonly viewer: SessionViewerResult;
  readonly displayRole: SessionViewerOrganizationRole | undefined;
  readonly onNavigate: (
    section: 'clients' | 'finance' | 'servers',
    directoryScreen?: 'clients' | 'prospects',
  ) => void;
}): React.JSX.Element {
  const isAdministratorView = displayRole === 'ORGANIZATION_ADMIN';
  const isProviderView = displayRole === 'PROVIDER';
  const [clientTotals, setClientTotals] = useState<ClientDirectory | null>(null);
  useEffect(() => {
    if (!isAdministratorView) return;
    let active = true;
    void clientDirectoryApi.search('', 0).then(
      (result) => {
        if (active) setClientTotals(result);
      },
      () => {
        if (active) setClientTotals(null);
      },
    );
    return () => {
      active = false;
    };
  }, [isAdministratorView]);

  return (
    <div className="app-content">
      <div className="dashboard-heading">
        <div>
          <p className="dashboard-heading__eyebrow">Tableau de bord</p>
          <h1>Bonjour {viewer.givenName}.</h1>
          <p className="dashboard-heading__intro">
            Voici les indicateurs clés que Tosho vous propose en résumé
          </p>
        </div>
        {isAdministratorView ? (
          <div className="dashboard-heading__actions" aria-label="Actions métier à venir">
            <button className="dashboard-action dashboard-action--project" type="button" disabled>
              <PrototypeIcon name="folder-kanban" />
              Nouveau projet
            </button>
            <button className="dashboard-action dashboard-action--sav" type="button" disabled>
              <PrototypeIcon name="life-buoy" />
              Nouveau SAV
            </button>
            <button className="dashboard-action dashboard-action--faq" type="button" disabled>
              <PrototypeIcon name="book-open" />
              Nouvelle FAQ
            </button>
          </div>
        ) : null}
      </div>

      {isAdministratorView ? (
        <section className="dashboard-summary dashboard-summary--extended" aria-label="Indicateurs">
          {summaryCards.map((card) => {
            const count =
              card.label === 'Prospects'
                ? clientTotals?.sourceCount
                : card.label === 'Clients'
                  ? clientTotals?.clientCount
                  : undefined;
            return (
              <Fragment key={card.label}>
                <article className={`dashboard-summary-card ${card.className}`}>
                  {card.label === 'Prospects' || card.label === 'Clients' ? (
                    <button
                      type="button"
                      className="dashboard-summary-card__link"
                      aria-label={`Voir les ${card.label.toLowerCase()}`}
                      onClick={() =>
                        onNavigate('clients', card.label === 'Prospects' ? 'prospects' : 'clients')
                      }
                    />
                  ) : null}
                  <div className="dashboard-summary-card__header">
                    <span className="dashboard-summary-card__label">{card.label}</span>
                    <span className="dashboard-summary-card__icon" aria-hidden="true">
                      <PrototypeIcon name={card.icon} />
                    </span>
                  </div>
                  <strong
                    aria-label={
                      count === undefined ? `${card.label} sans donnée disponible` : undefined
                    }
                  >
                    {count ?? '—'}
                  </strong>
                  <small>{count === undefined ? 'En attente de données' : 'Données réelles'}</small>
                </article>
                {card.label === 'Clients' ? <DashboardLiveMetrics onNavigate={onNavigate} /> : null}
              </Fragment>
            );
          })}
        </section>
      ) : null}

      <section className="dashboard-grid">
        <article className="dashboard-panel dashboard-panel--wide">
          <header className="dashboard-panel__header">
            <div>
              <p className="dashboard-panel__eyebrow">Production</p>
              <h2>{isProviderView ? 'Mes interventions' : 'Avancement des projets'}</h2>
            </div>
          </header>
          <div className="dashboard-empty-state">
            <span className="dashboard-empty-state__mark" aria-hidden="true">
              <PrototypeIcon name="folder-kanban" />
            </span>
            <strong>Aucun projet à afficher</strong>
            <p>
              Les éléments autorisés pour ce profil apparaîtront ici dès qu'ils seront réellement
              disponibles.
            </p>
          </div>
        </article>

        <article className="dashboard-panel">
          <header className="dashboard-panel__header">
            <div>
              <p className="dashboard-panel__eyebrow">Activité</p>
              <h2>Derniers événements</h2>
            </div>
          </header>
          <div className="dashboard-empty-state dashboard-empty-state--compact">
            <span className="dashboard-empty-state__mark" aria-hidden="true">
              <PrototypeIcon name="bell" />
            </span>
            <strong>Aucune activité récente</strong>
            <p>Les événements autorisés apparaîtront ici sans donnée de démonstration.</p>
          </div>
        </article>
      </section>
      {isAdministratorView && (
        <HostingPleskAlerts
          organizationLabel={viewer.organization?.displayName ?? 'Organisation'}
          organizationLogoUrl={viewer.organization?.branding.logoUrl ?? null}
        />
      )}
    </div>
  );
}

function SupervisorHome({ viewer }: { readonly viewer: SessionViewerResult }): React.JSX.Element {
  return (
    <div className="app-content">
      <div className="dashboard-heading">
        <div>
          <p className="dashboard-heading__eyebrow">Supervision de plateforme</p>
          <h1>Bonjour {viewer.givenName}.</h1>
          <p className="dashboard-heading__intro">
            Cet espace reste séparé des données métier des organisations. Seuls les administrateurs
            d'organisation et les préférences de plateforme y seront exposés.
          </p>
        </div>
      </div>

      <section className="dashboard-grid dashboard-grid--supervisor">
        <article className="dashboard-panel">
          <header className="dashboard-panel__header">
            <div>
              <p className="dashboard-panel__eyebrow">Supervision</p>
              <h2>Administrateurs d'organisation</h2>
            </div>
          </header>
          <div className="dashboard-empty-state dashboard-empty-state--compact">
            <span className="dashboard-empty-state__mark" aria-hidden="true">
              <PrototypeIcon name="users-round" />
            </span>
            <strong>Aucune donnée chargée</strong>
            <p>
              La liste réelle sera raccordée au lot de supervision sans accès aux projets, clients
              ou finances des organisations.
            </p>
          </div>
        </article>

        <article className="dashboard-panel">
          <header className="dashboard-panel__header">
            <div>
              <p className="dashboard-panel__eyebrow">Plateforme</p>
              <h2>Réglages</h2>
            </div>
          </header>
          <div className="dashboard-empty-state dashboard-empty-state--compact">
            <span className="dashboard-empty-state__mark" aria-hidden="true">
              <PrototypeIcon name="settings" />
            </span>
            <strong>Configuration non chargée</strong>
            <p>
              Les réglages globaux seront affichés ici lorsqu'ils disposeront de leurs sources
              persistantes.
            </p>
          </div>
        </article>
      </section>
    </div>
  );
}

function GlobalSearchDialog({
  onClose,
  administrator,
  onChoose,
}: {
  readonly onClose: () => void;
  readonly administrator: boolean;
  readonly onChoose: (result: GlobalSearchResult) => void;
}): React.JSX.Element {
  useCloseOnEscape(onClose);

  useEffect(() => {
    const previous = document.activeElement;
    return () => {
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);

  return (
    <ModalBackdrop
      className="tosho-modal-backdrop global-search-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
      aria-label="Recherche globale"
      onKeyDown={(event) => {
        if (event.key !== 'Tab') return;
        const controls = Array.from(
          event.currentTarget.querySelectorAll<HTMLElement>(
            'button:not([disabled]):not([tabindex="-1"]), input:not([disabled])',
          ),
        );
        const first = controls[0],
          last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }}
      close={
        <ModalCloseButton
          className="tosho-modal-close global-search-dialog__close"
          onClick={onClose}
          aria-label="Fermer la recherche"
          title="Fermer"
        />
      }
    >
      <section className="tosho-modal-panel global-search-dialog">
        <GlobalSearch administrator={administrator} onChoose={onChoose} autoFocus dialog />
      </section>
    </ModalBackdrop>
  );
}

function ChangelogDialog({ onClose }: { readonly onClose: () => void }): React.JSX.Element {
  useCloseOnEscape(onClose);

  return (
    <ModalBackdrop
      className="tosho-modal-backdrop changelog-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
      aria-labelledby="changelog-title"
      close={
        <ModalCloseButton
          className="tosho-modal-close changelog-dialog__close"
          onClick={onClose}
          aria-label="Fermer le changelog"
          title="Fermer"
        />
      }
    >
      <section className="tosho-modal-panel changelog-dialog">
        <header className="changelog-dialog__header tosho-modal-header">
          <div>
            <p>Évolutions publiées</p>
            <h2 id="changelog-title">Changelog Tosho</h2>
          </div>
        </header>
        <div className="changelog-dialog__body">
          <p className="changelog-dialog__intro">
            Les principales améliorations visibles de Tosho, expliquées simplement.
          </p>
          <ol className="changelog-list">
            {TOSHO_PRODUCT_CHANGELOG.map((entry) => (
              <li className="changelog-entry" key={`${entry.version}-${entry.date}`}>
                <div className="changelog-entry__meta">
                  <strong>{entry.version}</strong>
                  <time>{entry.date}</time>
                </div>
                <h3>{entry.title}</h3>
                <ul>
                  {entry.changes.map((change) => (
                    <li key={change}>{change}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </ModalBackdrop>
  );
}

function WhyToshoDialog({ onClose }: { readonly onClose: () => void }): React.JSX.Element {
  useCloseOnEscape(onClose);

  return (
    <ModalBackdrop
      className="tosho-modal-backdrop why-tosho-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
      aria-labelledby="why-tosho-title"
      close={
        <ModalCloseButton
          className="tosho-modal-close why-tosho-dialog__close"
          onClick={onClose}
          aria-label="Fermer"
          title="Fermer"
        />
      }
    >
      <section className="tosho-modal-panel why-tosho-dialog">
        <header className="why-tosho-dialog__header tosho-modal-header">
          <img className="why-tosho-dialog__forge" src={authForge} alt="" aria-hidden="true" />
          <div className="why-tosho-dialog__veil" aria-hidden="true" />

          <div className="why-tosho-dialog__heading">
            <span className="why-tosho-dialog__kanji" lang="ja">
              刀匠
            </span>
            <h2 id="why-tosho-title">Pourquoi Tosho ?</h2>
            <p>La forge de KATANA</p>
          </div>
        </header>
        <div className="why-tosho-dialog__body">
          <p>
            <strong>Tōshō</strong> désigne le maître forgeron qui façonnait les lames des samouraïs
            dans le Japon féodal.
          </p>
          <p>
            Chez KATANA, chaque projet client est une lame forgée sur mesure : un outil précis que
            nous plaçons entre ses mains pour affronter le monde des affaires.
          </p>
          <p>
            Tosho réunit le pilotage de notre activité, les projets, le SAV, les FAQ et notre
            mémoire technique. La plateforme apprend de nos réussites comme de nos erreurs pour
            rendre chaque réalisation plus solide que la précédente.
          </p>
          <button className="why-tosho-dialog__return" type="button" onClick={onClose}>
            Revenir à la forge
          </button>
        </div>
      </section>
    </ModalBackdrop>
  );
}

export function DashboardShell(props: DashboardShellProps): React.JSX.Element {
  return (
    <PaginationPreferences userId={props.viewer.userId}>
      <DashboardContent {...props} />
    </PaginationPreferences>
  );
}
